import { useState } from "react";
import { toast } from "sonner";
import { Loader2, UserPlus, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/adminApi";
import { validateRut, validateEmail, validateCelular, formatRut } from "@/lib/validation";
import { PRODUCT_OPTIONS, SUB_COBERTURAS } from "@/constants/site";

const INDUSTRIAS = PRODUCT_OPTIONS;

const CANALES = [
  { value: "formulario", label: "Formulario" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "mail", label: "Mail" },
  { value: "referido", label: "Referido" },
];

const EMPTY = {
  nombre: "", rut: "", celular: "", email: "",
  tipo_industria: [], sub_coberturas: [], equipo_descripcion: "",
  canal: "formulario", ref: "",
};

export function NewLeadDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleIndustria = (v) => setForm((f) => {
    const next = f.tipo_industria.includes(v)
      ? f.tipo_industria.filter((x) => x !== v)
      : [...f.tipo_industria, v];
    return {
      ...f,
      tipo_industria: next,
      sub_coberturas: next.includes("colaboradores") ? f.sub_coberturas : [],
    };
  });
  const toggleSub = (v) => setForm((f) => ({
    ...f,
    sub_coberturas: f.sub_coberturas.includes(v)
      ? f.sub_coberturas.filter((x) => x !== v)
      : [...f.sub_coberturas, v],
  }));

  const submit = async () => {
    if (!form.nombre.trim()) return toast.error("Ingresa nombre o razón social");
    if (!validateRut(form.rut)) return toast.error("RUT inválido");
    if (!validateCelular(form.celular)) return toast.error("Teléfono inválido");
    if (!validateEmail(form.email)) return toast.error("Email inválido");
    if (form.tipo_industria.length === 0) return toast.error("Selecciona al menos un producto");
    setSaving(true);
    try {
      await api.post("/admin/leads", { ...form, elapsed_ms: 999999 });
      toast.success("Lead creado");
      setForm(EMPTY);
      onCreated?.();
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo crear el lead");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="new-lead-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-brand-gold" /> Nuevo lead</DialogTitle>
          <DialogDescription>Carga manual desde el panel (sin anti-spam ni emails automáticos).</DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <div>
            <Label>Nombre o razón social *</Label>
            <Input data-testid="nl-nombre" value={form.nombre} onChange={(e) => update("nombre", e.target.value)} placeholder="Constructora Los Andes SpA" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>RUT *</Label>
              <Input data-testid="nl-rut" value={form.rut} onChange={(e) => update("rut", formatRut(e.target.value))} placeholder="76.543.210-K" maxLength={12} />
            </div>
            <div>
              <Label>Teléfono *</Label>
              <Input data-testid="nl-celular" value={form.celular} onChange={(e) => update("celular", e.target.value)} placeholder="+56 9 1234 5678" />
            </div>
          </div>
          <div>
            <Label>Email *</Label>
            <Input data-testid="nl-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="cliente@empresa.cl" />
          </div>
          <div>
            <Label>Producto(s) *</Label>
            <div className="grid grid-cols-2 gap-1.5 mt-1">
              {INDUSTRIAS.map((opt) => {
                const active = form.tipo_industria.includes(opt.value);
                return (
                  <button type="button" key={opt.value} data-testid={`nl-industria-${opt.value}`} onClick={() => toggleIndustria(opt.value)}
                    className={`text-left px-3 py-1.5 rounded-lg border-2 text-sm font-medium transition-colors ${active ? "border-brand-yellow bg-brand-yellow/10 text-brand-ink" : "border-gray-200 bg-white text-gray-600 hover:border-brand-yellow/40"}`}>
                    {active && <CheckCircle2 className="inline h-3.5 w-3.5 mr-1 text-brand-gold" />}
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          {form.tipo_industria.includes("colaboradores") && (
            <div className="rounded-xl border border-brand-yellow/50 bg-brand-yellow/10 p-3" data-testid="nl-sub-block">
              <Label className="text-[13px]">Coberturas de colaboradores (opcional)</Label>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {SUB_COBERTURAS.map((sub) => {
                  const on = form.sub_coberturas.includes(sub.id);
                  return (
                    <button type="button" key={sub.id} data-testid={`nl-sub-${sub.id}`} onClick={() => toggleSub(sub.id)}
                      className={`rounded-full border px-2.5 py-1 text-[11.5px] font-medium transition-colors ${on ? "border-brand-ink bg-brand-ink text-white" : "border-brand-ink/20 bg-white text-brand-ink hover:border-brand-ink/50"}`}>
                      {sub.short}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div>
            <Label>Equipo / detalle (opcional)</Label>
            <Textarea data-testid="nl-equipo" rows={2} value={form.equipo_descripcion} onChange={(e) => update("equipo_descripcion", e.target.value)} placeholder="Ej: planta de 3.000 m², 2 grúas horquilla, 8 camionetas..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Canal</Label>
              <Select value={form.canal} onValueChange={(v) => update("canal", v)}>
                <SelectTrigger data-testid="nl-canal"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CANALES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Referido (código afiliado)</Label>
              <Input data-testid="nl-ref" value={form.ref} onChange={(e) => update("ref", e.target.value.toUpperCase())} placeholder="AFF123" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="nl-submit" className="bg-brand-ink hover:bg-brand-inkHover text-white">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Guardando</> : "Crear lead"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
